import { computed } from 'vue'

import { ScopeKind } from '@/enums/group'
import i18n from '@/libs/i18n'
import { useScopeStore } from '@/stores/scope'

/**
 * The scope, as the consolidated endpoints take it: `?properties=<id>,<id>`.
 *
 * # Why the whole group sends NOTHING
 *
 * Every G1 read is filtered by the account's own scope on the server whether this console
 * names houses or not. Naming them adds one thing — a NARROWING — and that is exactly
 * what „cela skupina“ is not asking for. Sending the ids anyway would send the ids of the
 * tree this browser last read, so a hotel opened this morning would be missing from the
 * group's own total until somebody reloaded the page, and missing silently.
 *
 * So the whole group is the absence of the key, and the three narrowed states are the
 * list. The server resolves both; only one of them can go stale.
 *
 * # An empty narrowing is not the whole group
 *
 * A region with no houses in it resolves to an empty list, and an empty `properties=`
 * could be read by a service as "no filter". Screens therefore do not CALL on an empty
 * scope: `covers` says so, and they draw their empty state instead. The alternative is a
 * board that answers for forty hotels under the heading of a region with none.
 */
export const useScopeParams = () => {
    const scopeStore = useScopeStore()

    /** Whether the scope covers any house at all — false only for a narrowing that resolved to none. */
    const covers = computed((): boolean =>
        scopeStore.kind === ScopeKind.Group || scopeStore.propertyIds.length > 0)

    /**
     * A path with the scope and whatever else the screen asks for on it.
     *
     * Parameters with no value are left out rather than sent empty: `?date=` is a value
     * somebody typed nothing into, and a service reading it as "all dates" is a bug
     * neither side wrote.
     */
    const scopedPath = (path: string, params: Record<string, string | number | undefined> = {}): string => {
        const query = new URLSearchParams()

        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== '') query.set(key, String(value))
        }

        if (scopeStore.kind !== ScopeKind.Group) {
            query.set('properties', scopeStore.propertyIds.join(','))
        }

        const rendered = query.toString()

        return rendered ? `${path}?${rendered}` : path
    }

    /**
     * What a screen watches to know the question changed.
     *
     * Two things change it and both are here: the SELECTION (a different region) and the
     * TREE (the selection resolving to different houses once it has arrived, which is
     * every first page load). Watching `propertyIds` itself would fire on every read of
     * the tree, because a computed array is a new array.
     */
    const scopeKey = computed((): string =>
        `${scopeStore.queryValue}|${scopeStore.propertyIds.length}`)

    /**
     * WHAT IS BEING LOOKED AT, in words — the name of the thing chosen, never the word
     * for its kind.
     *
     * It began as the scope selector's own label and is shared because two more places
     * now have to say the same thing: the chip over Milo's question box, which is what
     * tells somebody that an answer is about a region rather than about the group, and
     * the report dialog, which offers to freeze that narrowing into a report. Three
     * copies would be three chances for the chip to name a region the console is not
     * actually showing.
     */
    const scopeLabel = computed((): string => {
        const t = i18n.global.t

        switch (scopeStore.kind) {
            case ScopeKind.Unit:
                return scopeStore.branches.find(unit => unit.id === scopeStore.unitId)?.name
                    ?? t('scope.kind_unit')

            case ScopeKind.Entity:
                return scopeStore.entities.find(entity => entity.id === scopeStore.entityId)?.name
                    ?? t('scope.kind_entity')

            case ScopeKind.Properties:
                return scopeStore.propertyIds.length === 1
                    ? scopeStore.selectedProperties[0]?.name ?? t('scope.kind_properties')
                    : t('scope.picked', { count: scopeStore.propertyIds.length })

            default:
                return scopeStore.rootName || t('scope.kind_group')
        }
    })

    /**
     * The same narrowing for a call that carries it in the BODY rather than in a query.
     *
     * `POST /group/chat` takes `properties` as a field of the question, and a report
     * stores the ids it answers for. The rule above is unchanged and is the reason this
     * is `undefined` and not `[]` for the whole group: an empty list in a body is a
     * narrowing to nothing, and the absence of the key is the group.
     */
    const scopeProperties = computed((): string[] | undefined =>
        scopeStore.kind === ScopeKind.Group ? undefined : [...scopeStore.propertyIds])

    return { covers, scopedPath, scopeKey, scopeProperties, scopeLabel }
}
